import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Brain, FloppyDisk, Sparkle, Robot, Clock, Storefront, CalendarCheck } from "@phosphor-icons/react";
import { toast } from "sonner";

const DAYS = ["Lun", "Mar", "Mie", "Jue", "Vie", "Sab", "Dom"];

function defaultHours() {
  return Array.from({ length: 7 }, (_, i) => ({
    day: i, enabled: i < 6, open: "09:00", close: "18:00",
  }));
}

export default function Agent() {
  const [cfg, setCfg] = useState(null);
  const [models, setModels] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/agent-config").then((r) => setCfg(r.data));
    api.get("/ai/models").then((r) => setModels(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/agent-config", {
        ...cfg,
        fallback_to_human_keywords: typeof cfg.fallback_to_human_keywords === "string"
          ? cfg.fallback_to_human_keywords.split(",").map((s) => s.trim()).filter(Boolean)
          : cfg.fallback_to_human_keywords,
      });
      toast.success("Configuracion guardada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return <div className="p-8 text-[#A1A1AA] text-sm">Cargando...</div>;

  const keywords = Array.isArray(cfg.fallback_to_human_keywords)
    ? cfg.fallback_to_human_keywords.join(", ")
    : cfg.fallback_to_human_keywords || "";

  const currentModel = `${cfg.ai_provider || "openai"}:${cfg.ai_model || "gpt-5.2"}`;
  const pickModel = (key) => {
    const [provider, model] = key.split(":");
    setCfg({ ...cfg, ai_provider: provider, ai_model: model });
  };

  const hours = (cfg.business_hours && cfg.business_hours.length === 7) ? cfg.business_hours : defaultHours();
  const setHour = (idx, patch) => {
    const next = hours.map((h, i) => (i === idx ? { ...h, ...patch } : h));
    setCfg({ ...cfg, business_hours: next });
  };

  return (
    <div className="p-8 max-w-4xl mx-auto fade-in">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#71717A] mb-2">Inteligencia Artificial</div>
          <h1 className="font-display text-4xl font-medium tracking-tighter">Agente IA</h1>
          <p className="text-sm text-[#A1A1AA] mt-1">Configura como tu vendedor IA responde a los clientes.</p>
        </div>
        <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-[#121214] border border-[#27272A]">
          <span className="text-xs text-[#A1A1AA]">IA activa</span>
          <Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="agent-enabled-switch" />
        </div>
      </div>

      <div className="space-y-5">
        <div className="border border-[#27272A] bg-[#121214] rounded-lg p-6">
          <div className="flex items-center gap-2 mb-1">
            <Robot size={16} className="text-[#5E6AD2]" weight="fill" />
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Motor de IA</div>
          </div>
          <h3 className="font-display text-xl font-medium tracking-tight mb-2">Modelo de lenguaje</h3>
          <p className="text-xs text-[#71717A] mb-4">Elige qué modelo responderá a tus clientes.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="agent-model-grid">
            {models.map((m) => {
              const key = `${m.provider}:${m.model}`;
              const active = currentModel === key;
              return (
                <button
                  type="button"
                  key={key}
                  onClick={() => pickModel(key)}
                  data-testid={`model-card-${m.provider}`}
                  className={`text-left p-4 rounded-lg border transition-all ${
                    active
                      ? "border-[#5E6AD2] bg-[#5E6AD2]/10 ring-1 ring-[#5E6AD2]"
                      : "border-[#27272A] bg-[#0A0A0C] hover:border-[#3F3F46]"
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">{m.vendor}</div>
                    {active && <span className="h-2 w-2 rounded-full bg-[#5E6AD2]" />}
                  </div>
                  <div className="font-display text-base font-medium tracking-tight">{m.label}</div>
                  <div className="text-[10px] text-[#71717A] mt-1 truncate font-mono-jb">{m.model}</div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="border border-[#27272A] bg-[#121214] rounded-lg p-6">
          <div className="flex items-center gap-2 mb-1">
            <Storefront size={16} className="text-emerald-400" weight="fill" />
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Automatizacion</div>
          </div>
          <h3 className="font-display text-xl font-medium tracking-tight mb-2">Modos activos</h3>
          <p className="text-xs text-[#71717A] mb-4">Activa lo que tu negocio maneja. La IA clasificará cada conversación y creará pedidos o citas automáticamente.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
            <div className={`p-4 rounded-lg border ${cfg.delivery_enabled ? "border-emerald-500/60 bg-emerald-500/5" : "border-[#27272A] bg-[#0A0A0C]"}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Storefront size={16} className={cfg.delivery_enabled ? "text-emerald-400" : "text-[#71717A]"} weight="fill" />
                  <div className="font-medium text-sm">Pedidos / Delivery</div>
                </div>
                <Switch
                  checked={!!cfg.delivery_enabled}
                  onCheckedChange={(v) => setCfg({ ...cfg, delivery_enabled: v })}
                  data-testid="toggle-delivery"
                />
              </div>
              <div className="text-[11px] text-[#71717A]">La IA captura items, dirección y total. Se guardan en /app/orders.</div>
            </div>
            <div className={`p-4 rounded-lg border ${cfg.appointments_enabled ? "border-[#5E6AD2]/60 bg-[#5E6AD2]/5" : "border-[#27272A] bg-[#0A0A0C]"}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <CalendarCheck size={16} className={cfg.appointments_enabled ? "text-[#5E6AD2]" : "text-[#71717A]"} weight="fill" />
                  <div className="font-medium text-sm">Citas</div>
                </div>
                <Switch
                  checked={!!cfg.appointments_enabled}
                  onCheckedChange={(v) => setCfg({ ...cfg, appointments_enabled: v })}
                  data-testid="toggle-appointments"
                />
              </div>
              <div className="text-[11px] text-[#71717A]">La IA agenda fecha+hora y se reflejan en /app/calendar.</div>
            </div>
          </div>

          {cfg.delivery_enabled && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 pb-4 border-b border-[#27272A]">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-[#71717A] mb-1.5 block">Costo de envío ($)</Label>
                <Input
                  type="number"
                  step="1"
                  value={cfg.delivery_fee ?? 0}
                  onChange={(e) => setCfg({ ...cfg, delivery_fee: parseFloat(e.target.value || "0") })}
                  className="bg-[#0A0A0C] border-[#27272A]"
                  data-testid="delivery-fee"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-[#71717A] mb-1.5 block">Zonas de cobertura</Label>
                <Input
                  value={cfg.delivery_zones || ""}
                  onChange={(e) => setCfg({ ...cfg, delivery_zones: e.target.value })}
                  placeholder="Polanco, Roma Norte, Condesa"
                  className="bg-[#0A0A0C] border-[#27272A]"
                  data-testid="delivery-zones"
                />
              </div>
              <div className="md:col-span-2 mt-2 rounded-lg border border-[#27272A] bg-[#0A0A0C] p-4 space-y-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-sm font-medium">Flujo estricto de pedido</div>
                    <div className="text-[11px] text-[#71717A] max-w-md leading-relaxed">
                      La IA sigue los 6 pasos obligatorios: 1) qué pedir, 2) confirmar total, 3) domicilio o recoger, 4) dirección si es domicilio, 5) comprobante si aplica, 6) cerrar.
                      Una pregunta por mensaje, sin repeticiones.
                    </div>
                  </div>
                  <Switch
                    checked={cfg.strict_order_flow !== false}
                    onCheckedChange={(v) => setCfg({ ...cfg, strict_order_flow: v })}
                    data-testid="strict-order-flow"
                  />
                </div>

                <div className="pt-4 border-t border-[#27272A]">
                  <div className="text-sm font-medium mb-1">Comprobante de pago</div>
                  <div className="text-[11px] text-[#71717A] mb-3">Elige para qué tipo de pedido se exige foto del pago antes de confirmar.</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${cfg.require_proof_delivery ? "border-emerald-500/60 bg-emerald-500/5" : "border-[#27272A] bg-[#121214]"}`}>
                      <input
                        type="checkbox"
                        checked={!!cfg.require_proof_delivery}
                        onChange={(e) => setCfg({ ...cfg, require_proof_delivery: e.target.checked })}
                        data-testid="require-proof-delivery"
                        className="mt-0.5 h-4 w-4 rounded border-[#3F3F46] bg-[#0A0A0C] text-emerald-500"
                      />
                      <div>
                        <div className="text-sm font-medium">Pedidos a domicilio</div>
                        <div className="text-[11px] text-[#71717A]">Exigir comprobante antes de registrar</div>
                      </div>
                    </label>
                    <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${cfg.require_proof_pickup ? "border-[#5E6AD2]/60 bg-[#5E6AD2]/5" : "border-[#27272A] bg-[#121214]"}`}>
                      <input
                        type="checkbox"
                        checked={!!cfg.require_proof_pickup}
                        onChange={(e) => setCfg({ ...cfg, require_proof_pickup: e.target.checked })}
                        data-testid="require-proof-pickup"
                        className="mt-0.5 h-4 w-4 rounded border-[#3F3F46] bg-[#0A0A0C] text-[#5E6AD2]"
                      />
                      <div>
                        <div className="text-sm font-medium">Pedidos para recoger</div>
                        <div className="text-[11px] text-[#71717A]">Exigir comprobante antes de registrar</div>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="pt-4 border-t border-[#27272A]">
                  <div className="text-sm font-medium mb-1">Métodos de pago aceptados</div>
                  <div className="text-[11px] text-[#71717A] mb-3">La IA le ofrecerá al cliente solamente estos métodos.</div>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { k: "efectivo", l: "Efectivo" },
                      { k: "transferencia", l: "Transferencia" },
                      { k: "nequi", l: "Nequi" },
                      { k: "daviplata", l: "Daviplata" },
                      { k: "bancolombia", l: "Bancolombia a la mano" },
                      { k: "contra_entrega", l: "Contra entrega" },
                      { k: "tarjeta", l: "Tarjeta (link)" },
                      { k: "qr", l: "QR" },
                      { k: "paypal", l: "PayPal" },
                      { k: "zelle", l: "Zelle" },
                    ].map((m) => {
                      const list = cfg.payment_methods || [];
                      const on = list.includes(m.k);
                      return (
                        <button
                          type="button"
                          key={m.k}
                          onClick={() =>
                            setCfg({
                              ...cfg,
                              payment_methods: on
                                ? list.filter((x) => x !== m.k)
                                : [...list, m.k],
                            })
                          }
                          data-testid={`pm-${m.k}`}
                          className={`px-3 h-8 text-xs rounded-full border transition-colors ${
                            on
                              ? "bg-[#5E6AD2] border-[#5E6AD2] text-white"
                              : "bg-[#121214] border-[#27272A] text-[#A1A1AA] hover:text-white"
                          }`}
                        >
                          {m.l}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {(cfg.require_proof_delivery || cfg.require_proof_pickup) && (
                  <div className="pt-4 border-t border-[#27272A]">
                    <Label className="text-[11px] uppercase tracking-wider text-[#71717A] mb-1.5 block">
                      Datos de pago (se copian textual al cliente al pedir comprobante)
                    </Label>
                    <Textarea
                      value={cfg.payment_instructions || ""}
                      onChange={(e) => setCfg({ ...cfg, payment_instructions: e.target.value })}
                      rows={4}
                      placeholder={"Nequi 3101234567\nTitular: Juan Pérez\nConcepto: pedido"}
                      className="bg-[#121214] border-[#27272A] font-mono-jb text-xs"
                      data-testid="payment-instructions"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {cfg.appointments_enabled && (
            <div className="space-y-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-[#71717A] mb-1.5 block">Servicios (uno por línea)</Label>
                <Textarea
                  value={cfg.services || ""}
                  onChange={(e) => setCfg({ ...cfg, services: e.target.value })}
                  rows={4}
                  placeholder="Corte de cabello&#10;Manicura&#10;Masaje 60 min&#10;Consulta nutricional"
                  className="bg-[#0A0A0C] border-[#27272A] font-mono-jb text-xs"
                  data-testid="agent-services"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-[#71717A] mb-1.5 block">Duración por defecto (min)</Label>
                <Input
                  type="number"
                  value={cfg.appointment_duration_min ?? 30}
                  onChange={(e) => setCfg({ ...cfg, appointment_duration_min: parseInt(e.target.value || "30", 10) })}
                  className="bg-[#0A0A0C] border-[#27272A] w-32"
                  data-testid="appointment-duration"
                />
              </div>
            </div>
          )}
        </div>

        <div className="border border-[#27272A] bg-[#121214] rounded-lg p-6">
          <div className="flex items-center gap-2 mb-1">
            <Clock size={16} className="text-amber-400" weight="fill" />
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Disponibilidad</div>
          </div>
          <h3 className="font-display text-xl font-medium tracking-tight mb-2">Horarios de atención</h3>
          <p className="text-xs text-[#71717A] mb-4">La IA usa estos horarios para sugerir franjas de citas y avisar cuando estás cerrado.</p>

          <div className="space-y-2" data-testid="business-hours-grid">
            {hours.map((h, idx) => (
              <div key={idx} className="flex items-center gap-3 py-1.5">
                <div className="w-12 text-xs uppercase tracking-wider text-[#A1A1AA]">{DAYS[h.day] || DAYS[idx]}</div>
                <Switch
                  checked={!!h.enabled}
                  onCheckedChange={(v) => setHour(idx, { enabled: v })}
                  data-testid={`hours-toggle-${idx}`}
                />
                {h.enabled ? (
                  <>
                    <Input
                      type="time"
                      value={h.open || "09:00"}
                      onChange={(e) => setHour(idx, { open: e.target.value })}
                      className="bg-[#0A0A0C] border-[#27272A] w-28 h-8 text-xs"
                      data-testid={`hours-open-${idx}`}
                    />
                    <span className="text-[#71717A] text-xs">a</span>
                    <Input
                      type="time"
                      value={h.close || "18:00"}
                      onChange={(e) => setHour(idx, { close: e.target.value })}
                      className="bg-[#0A0A0C] border-[#27272A] w-28 h-8 text-xs"
                      data-testid={`hours-close-${idx}`}
                    />
                  </>
                ) : (
                  <span className="text-xs text-[#71717A] italic">Cerrado</span>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="border border-[#27272A] bg-[#121214] rounded-lg p-6">
          <div className="flex items-center gap-2 mb-1">
            <Brain size={16} className="text-[#5E6AD2]" weight="fill" />
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Personalidad</div>
          </div>
          <h3 className="font-display text-xl font-medium tracking-tight mb-4">Tono y estilo</h3>
          <Input
            value={cfg.tone} onChange={(e) => setCfg({ ...cfg, tone: e.target.value })}
            placeholder="amigable y profesional"
            data-testid="agent-tone"
            className="bg-[#0A0A0C] border-[#27272A]"
          />
        </div>

        <div className="border border-[#27272A] bg-[#121214] rounded-lg p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Instrucciones especiales</div>
          <h3 className="font-display text-xl font-medium tracking-tight mb-2">Personality prompt</h3>
          <p className="text-xs text-[#71717A] mb-3">Persona, do&apos;s &amp; don&apos;ts, frases del brand voice. Se anade al system prompt de la IA.</p>
          <Textarea
            value={cfg.personality_prompt || ""} onChange={(e) => setCfg({ ...cfg, personality_prompt: e.target.value })}
            placeholder="Eres Luna, asistente virtual de ventas. Siempre saluda por nombre, usa el voseo si el cliente es de Argentina, nunca digas 'estoy programada para'..."
            rows={4}
            data-testid="agent-personality"
            className="bg-[#0A0A0C] border-[#27272A] font-mono-jb text-xs"
          />
        </div>

        <div className="border border-[#27272A] bg-[#121214] rounded-lg p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Contexto</div>
          <h3 className="font-display text-xl font-medium tracking-tight mb-4">Descripcion del negocio</h3>
          <Textarea
            value={cfg.business_description} onChange={(e) => setCfg({ ...cfg, business_description: e.target.value })}
            placeholder="Somos una empresa SaaS que ofrece..."
            rows={3}
            data-testid="agent-business"
            className="bg-[#0A0A0C] border-[#27272A] font-mono-jb text-xs"
          />
        </div>

        <div className="border border-[#27272A] bg-[#121214] rounded-lg p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Catalogo estructurado</div>
          <h3 className="font-display text-xl font-medium tracking-tight mb-2">Catalogo</h3>
          <p className="text-xs text-[#71717A] mb-3">Un producto por linea: nombre - precio - descripcion breve. La IA solo cita lo que este aqui.</p>
          <Textarea
            value={cfg.catalog || ""} onChange={(e) => setCfg({ ...cfg, catalog: e.target.value })}
            placeholder="Plan Starter - $499/mes - hasta 1000 mensajes IA&#10;Plan Pro - $999/mes - mensajes ilimitados&#10;Plan Business - $2499/mes - multi-numero y equipo"
            rows={6}
            data-testid="agent-catalog"
            className="bg-[#0A0A0C] border-[#27272A] font-mono-jb text-xs"
          />
        </div>

        <div className="border border-[#27272A] bg-[#121214] rounded-lg p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Legacy (free-text)</div>
          <h3 className="font-display text-xl font-medium tracking-tight mb-4">Productos y servicios (descripcion libre)</h3>
          <Textarea
            value={cfg.products_services} onChange={(e) => setCfg({ ...cfg, products_services: e.target.value })}
            placeholder="Plan Basic $499/mes - incluye...&#10;Plan Pro $999/mes - incluye..."
            rows={4}
            data-testid="agent-products"
            className="bg-[#0A0A0C] border-[#27272A] font-mono-jb text-xs"
          />
        </div>

        <div className="border border-[#27272A] bg-[#121214] rounded-lg p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Knowledge base</div>
          <h3 className="font-display text-xl font-medium tracking-tight mb-4">Preguntas frecuentes</h3>
          <Textarea
            value={cfg.faqs} onChange={(e) => setCfg({ ...cfg, faqs: e.target.value })}
            placeholder="P: Tienen prueba gratis? R: Si, 14 dias.&#10;P: Aceptan tarjeta? R: Si..."
            rows={6}
            data-testid="agent-faqs"
            className="bg-[#0A0A0C] border-[#27272A] font-mono-jb text-xs"
          />
        </div>

        <div className="border border-[#27272A] bg-[#121214] rounded-lg p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Cierre de venta</div>
          <h3 className="font-display text-xl font-medium tracking-tight mb-4">Estrategia</h3>
          <Textarea
            value={cfg.closing_strategy} onChange={(e) => setCfg({ ...cfg, closing_strategy: e.target.value })}
            rows={3}
            data-testid="agent-closing"
            className="bg-[#0A0A0C] border-[#27272A] font-mono-jb text-xs"
          />
        </div>

        <div className="border border-[#27272A] bg-[#121214] rounded-lg p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Escalacion</div>
          <h3 className="font-display text-xl font-medium tracking-tight mb-2">Palabras que escalan a humano</h3>
          <p className="text-xs text-[#71717A] mb-4">Si el cliente menciona estas palabras, la IA dejara la conversacion al equipo humano. Separa por comas.</p>
          <Input
            value={keywords}
            onChange={(e) => setCfg({ ...cfg, fallback_to_human_keywords: e.target.value })}
            placeholder="humano, agente, persona, asesor"
            data-testid="agent-keywords"
            className="bg-[#0A0A0C] border-[#27272A] font-mono-jb text-xs"
          />
        </div>

        <div className="sticky bottom-4 z-10 flex justify-end">
          <Button onClick={save} disabled={saving} className="bg-[#5E6AD2] hover:bg-[#4B55C3] shadow-xl" data-testid="save-agent-button">
            <FloppyDisk size={14} className="mr-1.5" weight="fill" />
            {saving ? "Guardando..." : "Guardar cambios"}
          </Button>
        </div>
      </div>
    </div>
  );
}
